import {
  Dropdown,
  ICommandBarItemProps,
  IDropdownOption,
  PrimaryButton,
  TooltipHost,
} from "@fluentui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import CustomCommandBar from "../../Components/CustomCommandBar/CustomCommandBar";
import DataTable from "../../Components/DataTable/DataTable";
import Loadable from "../../Components/Loadable/Loadable";
import { AppRoutes } from "../../Constants/AppRoutes";
import { IAlarm, isTestAlarm, testAlarmNote } from "../../Models/IAlarm";
import TestBadge from "../../Components/TestBadge/TestBadge";
import { ISensor } from "../../Models/ISensor";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { getSensorIconTooltip } from "../../Utils/SensorMaps";
import { ToastMessage } from "../../Utils/UIMessages";
import "./AlarmsPage.scss";
import { GlobalState } from "../../Redux/RootReducer";
import { useSelector } from "react-redux";
import { IUserMinimal } from "../../Models/IUser";
import NavigationMenu from "../../Components/NavigationMenu/NavigationMenu";

const AlarmsPage = () => {
  const users = useSelector((s: GlobalState) => s.generic.users);

  const [loading, setLoading] = useState<boolean>(false);
  const [alarms, setAlarms] = useState<IAlarm[]>([]);
  const [limit, setLimit] = useState<number>(1000);
  const [max, setMax] = useState<number>(0);
  const loadAlarms = () => {
    setLoading(true);
    ApiService.AlarmsController.GetPaginatedAlarms(
      0,
      limit,
      (response: IAPIResponse) => {
        if (response.error === null) {
          setMax(response.parsed?.maxAlarms ?? 0);
          setAlarms(response.parsed?.paginatedAlarms ?? []);
        } else {
          ToastMessage("error", "Impossibile caricare gli allarmi.");
        }
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    loadAlarms();

    document.addEventListener("reload-all-alarms", loadAlarms);

    return () => {
      document.removeEventListener("reload-all-alarms", loadAlarms);
    };
  }, [limit]);

  return (
    <div className="fade-in-whole-page">
      <CustomCommandBar
        specials={[
          <div className="command-bar-title">Allarmi sensori</div>,
          <NavigationMenu />,
        ]}
        buttons={[]}
      />
      <div className="sliding-page-content">
        <Loadable
          isLoading={loading}
          content={
            <div>
              <DataTable
                extraInnerProps={{
                  rowDynamiClass: {
                    className: "warn-red-alert-row",
                    conditionKey: "disabled",
                    value: true,
                  },
                  allowFilter: true,
                  extraHeaderItems: [
                    <span className="alarms-page-alarms-total">
                      Allarmi totali: {max}
                    </span>,
                    <Dropdown
                      selectedKey={limit}
                      onChange={(e: any, o: IDropdownOption | undefined) => {
                        setLimit(o?.key as any);
                      }}
                      options={[1000, 5000, 10000].map((x: number) => {
                        return {
                          key: x,
                          text: "Limite a " + x.toString() + " allarmi",
                        };
                      })}
                    />,
                  ],
                }}
                columns={[
                  {
                    name: "Data",
                    fieldName: "createdAt",
                    dataType: "date/time",
                    size: "small",
                  },
                  {
                    name: "Allarme",
                    fieldName: "type_",
                    dataType: "string",
                    size: "medium",
                    onRender: (item: any) => (
                      <div>
                        <div className="strict-flex-line" style={{ gap: "0.4em" }}>
                          <span>{item.type_}</span>
                          {isTestAlarm(item) && <TestBadge />}
                        </div>
                        {isTestAlarm(item) && (
                          <div style={{ fontSize: "0.8em", opacity: 0.8, whiteSpace: "normal" }}>
                            {testAlarmNote(item)}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    name: "Presa in carico",
                    fieldName: "updatedAt",
                    dataType: "date/time",
                    size: "small",
                  },
                  {
                    name: "Gestito da",
                    fieldName: "handledBy",
                    dataType: "string",
                    size: "small",
                  },
                  {
                    name: "Ospite",
                    fieldName: "patient.identifier",
                    dataType: "string",
                    size: "medium",
                  },
                  {
                    name: "Nota",
                    fieldName: "reason",
                    dataType: "string",
                    size: "medium",
                    onRender: (item: any) => {
                      if (item.disabled) {
                        return (
                          <TooltipHost
                            content={item.reason}
                            id={"table-row-item_state_" + item.id.toString()}
                          >
                            <div className="not-served-alarm-bubble">
                              NON GESTITO: {item.reason}
                            </div>
                          </TooltipHost>
                        );
                      } else if (
                        ["falso allarme", "rientrato"].filter((x: string) =>
                          (item.reason ?? "").toLocaleLowerCase().includes(x)
                        ).length > 0
                      ) {
                        return (
                          <div style={{ width: "fit-content" }}>
                            <TooltipHost
                              content={item.reason}
                              id={"table-row-item_state_" + item.id.toString()}
                            >
                              <div className="false-alarm-bubble">
                                {item.reason}
                              </div>
                            </TooltipHost>
                          </div>
                        );
                      } else {
                        return (
                          <div style={{ width: "fit-content" }}>
                            <TooltipHost
                              content={item.reason}
                              id={"table-row-item_state_" + item.id.toString()}
                            >
                              <div className="true-alarm-bubble">
                                {item.reason}
                              </div>
                            </TooltipHost>{" "}
                          </div>
                        );
                      }
                    },
                  },
                ]}
                rowData={alarms
                  .filter((x: IAlarm) => x.reason)
                  .map((x: IAlarm, i: number) => {
                    let user: IUserMinimal | undefined = users.find(
                      (y: IUserMinimal) => y.id === x.userId
                    );
                    let type_: string = getSensorIconTooltip(x.sensorType);

                    (x as any)["patient_position"] =
                      x.patient.floor +
                      ", " +
                      x.patient.room +
                      ", " +
                      x.patient.bed;

                    (x as any)["handledBy"] = user?.identifier;
                    (x as any)["type_"] = type_;
                    return x;
                  })}
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default AlarmsPage;
